import { Location } from '@angular/common';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { QuestionStatis, StudentScore } from '@shared/model/analysis.models';
import { filter } from 'rxjs';
import { AnswerReviewViewerComponent } from './answer-review-viewer.component';
export interface AnswerReviewViewerParams {
  questionStatis: QuestionStatis;
  currentQuestionNo: string;
  taskTitle: string;
  answer: StudentScore;
  // 进入查看器时作答列表的实际展示顺序
  sortedData: readonly StudentScore[];
  // 当前题目在题库里的 id，复核跳转时用于定位该题
  questionId: number;
  // 任务信息，复核跳转需要
  taskId: number;
  taskType: number;
  taskClassId: number | string;
  // 打标后回调，用于刷新作答详情主表
  onFlagChange: (student: StudentScore) => void;
}

/**
 * 作答复核查看器的全屏覆盖层
 *
 * 只负责覆盖层的创建/销毁与参数传递，业务状态在 AnswerReviewViewerComponent 内。
 */
@Injectable({ providedIn: 'root' })
export class AnswerReviewViewerService {
  private overlay = inject(Overlay);
  private location = inject(Location);
  private current?: OverlayRef;
  private subscriptions: { unsubscribe: () => void }[] = [];

  open(params: AnswerReviewViewerParams) {
    this.close();
    const overlay = this.overlay.create(
      new OverlayConfig({
        hasBackdrop: false,
        panelClass: 'size-full',
        disposeOnNavigation: true,
        scrollStrategy: this.overlay.scrollStrategies.block(),
      })
    );
    // 让浏览器返回可以关闭查看器，与全站图片查看器保持一致
    history.pushState({}, '', this.location.path(true));
    const ref = overlay.attach(new ComponentPortal(AnswerReviewViewerComponent));
    ref.setInput('questionStatis', params.questionStatis);
    ref.setInput('currentQuestionNo', params.currentQuestionNo);
    ref.setInput('taskTitle', params.taskTitle);
    ref.setInput('answer', params.answer);
    ref.setInput('sortedData', params.sortedData);
    ref.setInput('questionId', params.questionId);
    ref.setInput('taskId', params.taskId);
    ref.setInput('taskType', params.taskType);
    ref.setInput('taskClassId', params.taskClassId);

    this.current = overlay;
    this.subscriptions = [
      ref.instance.onClose.subscribe(() => overlay.detach()),
      ref.instance.flagChange.subscribe(student => params.onFlagChange(student)),
      // Esc 关闭
      overlay
        .keydownEvents()
        .pipe(filter(event => event.key === 'Escape'))
        .subscribe(() => overlay.detach()),
      // 返回、Esc、退出按钮任意方式关闭后释放覆盖层
      overlay.detachments().subscribe(() => this.close()),
    ];
  }

  close() {
    const overlay = this.current;
    this.current = undefined;
    for (const subscription of this.subscriptions) subscription.unsubscribe();
    this.subscriptions = [];
    overlay?.detach();
    overlay?.dispose();
  }
}
